import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import { Column, Row, useScreenClass } from "common/core/responsive";
import { Card } from "common/core/card";
import { DescriptionList, DescriptionListItem } from "common/core/description_list";
import {
  CountryFlag,
  IpAddress as IpAddressInline,
  IpAddressMap,
  IpCity,
  IpDeviceIcon,
  IpFormatDate,
} from "common/details/identity/signer/ip_address_section";
import { Heading } from "common/core/typography";
import { Attribute } from "common/details/identity/signer/attributes";
import { InfoTooltip } from "common/details/identity/v2/components/info_tooltip";
import { EXCLUDE_CLASS } from "util/sensitive";

import type { IpAddressCustomerSigner as CustomerSigner } from "./index.fragment.graphql";
import Styles from "./index.module.scss";

type Props = {
  customerSigner: CustomerSigner;
};

export function IpAddress({ customerSigner }: Props) {
  const { signerIdentities } = customerSigner;
  const screenClass = useScreenClass();

  const ipAddressLogs = useMemo(
    () =>
      signerIdentities
        ?.flatMap((identity) => identity.ipAddressLogs)
        .filter((log) => log !== null) ?? [],
    [signerIdentities],
  );

  const mostRecentIpLog = useMemo(
    () => signerIdentities?.[0].ipAddressLogs?.at(-1),
    [signerIdentities],
  );

  const homeAddressAttribute = useMemo(
    () =>
      signerIdentities?.[0].identityAttributes?.find(
        (attr) => attr.__typename === "IdentityAttributeHomeAddress",
      ),
    [signerIdentities],
  );

  const mapWidth = useMemo(() => {
    switch (screenClass) {
      case "lg":
      case "xl":
      case "xxl":
        return 640;
      default:
        return 400;
    }
  }, [screenClass]);

  if (!mostRecentIpLog) {
    return null;
  }

  return (
    <Row>
      <Column>
        <Card fullWidth noMargin>
          <Heading level="h4" textStyle="allCapsLabelSmall">
            <FormattedMessage
              id="764f5959-2566-4abc-9b09-b3897ccfd7d1"
              defaultMessage="Locations"
            />
          </Heading>
          <IpAddressMap
            ips={ipAddressLogs}
            homeAddress={homeAddressAttribute?.homeAddressValue}
            width={mapWidth}
            scale="2"
            className={classnames(Styles.map, EXCLUDE_CLASS)}
          />
          <DescriptionList className={Styles.information} itemGap="none">
            {homeAddressAttribute && (
              <Attribute attribute={homeAddressAttribute} automationId="identity-address-hidden" />
            )}
            <DescriptionListItem
              term={
                <>
                  <FormattedMessage
                    id="c38d59bc-466b-4079-a886-4b55941d9996"
                    defaultMessage="Last known location"
                  />
                  <InfoTooltip>
                    <FormattedMessage
                      id="0d691291-a934-4f4c-915a-6970296976b0"
                      defaultMessage="IP Address helps prevent fraud by understanding signer's location or VPN use"
                    />
                  </InfoTooltip>
                </>
              }
              definition={
                <div className={classnames(Styles.location, EXCLUDE_CLASS)}>
                  <div>
                    <CountryFlag {...mostRecentIpLog} />
                    <IpCity {...mostRecentIpLog} />
                  </div>
                  <div>
                    <IpDeviceIcon {...mostRecentIpLog} />
                    <IpAddressInline {...mostRecentIpLog} />
                    <IpFormatDate {...mostRecentIpLog} />
                  </div>
                </div>
              }
            />
          </DescriptionList>
        </Card>
      </Column>
    </Row>
  );
}
